import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { getFrnList, setFrnStatus } from "../../../redux/frnDetailsSlice";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { SwitchButton } from "../../../components/SwitchButton";
import { simpleTableCellStyle as cellStyle } from "../../../components/Common/simpleTableCellStyle";
import { Spinner, Toaster, tokens } from "@fluentui/react-components";

// Company Details -> FRN Details (§6.4, §6.5). FRN/Description are
// support-team-only, out-of-band — this page is read-only for those two
// columns. Active/Inactive is the one tenant-facing write: toggling it
// gates whether that FRN's data is included in Bulk Valuation (any
// provider, not just Transact).

const FrnDetails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast: notify, toasterId } = useToastMessage();
  const { listItems, loading, savingId } = useSelector((state: RootState) => state.frnDetails);

  useEffect(() => {
    dispatch(getFrnList());
  }, [dispatch]);

  const handleToggle = async (id: string, frn: string, nextValue: boolean) => {
    const result = await dispatch(setFrnStatus({ id, isActive: nextValue }));
    if (setFrnStatus.fulfilled.match(result)) {
      notify(nextValue ? "FRN Activated" : "FRN Deactivated", `FRN ${frn} is now ${nextValue ? "active" : "inactive"}.`);
    } else {
      notify("Error", (result as any).payload || "Failed to update FRN status.", "error");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div style={{ marginBottom: "16px" }}>
        <span style={{ fontSize: "18px", fontWeight: 600 }}>FRN Details</span>
        <p style={{ fontSize: "13px", color: tokens.colorNeutralForeground3, marginTop: "4px", maxWidth: "640px" }}>
          FCA firm reference numbers registered to this tenant. Only active FRNs are used for bulk valuation.
          To add or change an FRN, contact the support team.
        </p>
      </div>

      {loading ? (
        <Spinner label="Loading…" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cellStyle(true, false, true)}>FRN</th>
                <th style={cellStyle(false, false, true)}>Description</th>
                <th style={cellStyle(false, true, true)}>Status</th>
              </tr>
            </thead>
            <tbody>
              {listItems.length === 0 ? (
                <tr>
                  <td colSpan={3} style={cellStyle(true, true)}>
                    No FRNs are set up for this tenant. Contact the support team to add one.
                  </td>
                </tr>
              ) : (
                listItems.map((item) => (
                  <tr key={item.tenantFrnId}>
                    <td style={cellStyle(true, false)}>
                      <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{item.frn}</span>
                    </td>
                    <td style={cellStyle(false, false)}>
                      {item.description || <span style={{ color: tokens.colorNeutralForeground3 }}>No description</span>}
                    </td>
                    <td style={cellStyle(false, true)}>
                      <SwitchButton
                        itemChecked={item.isActive}
                        disabled={savingId === item.tenantFrnId}
                        label={item.isActive ? "Active" : "Inactive"}
                        labelPosition="after"
                        onChange={() => handleToggle(item.tenantFrnId, item.frn, !item.isActive)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FrnDetails;
